import PropTypes from 'prop-types';

import { GeolocationProvider } from '@/contexts/GeolocationContext';
import { GoogleMapsLoader } from '@/design-system/helpers/GoogleMapsLoader';

import { LocationHero } from './components/LocationHero';
import LocationMain from './components/LocationMain/LocationMain';
import { LocationFilterContextProvider } from './contexts/LocationFilterContext';
import { LocationSearchProvider } from './contexts/LocationSearchContext';
import { LocationStateContextProvider } from './contexts/LocationStateContext';
import { LocationUIContextProvider } from './contexts/LocationUIContext';
import styles from './LocationSearch.module.scss';

/**
 * The Location Search Page is the main search page for finding locations.
 */
const LocationSearch = ({
    state,
    title,
    description,
    searchLabel,
    searchErrorMessage,
    zipLabel,
    zipErrorMessage,
    breadcrumbs,
    typeAheadFixedItem,
    illustration,
    hideMobileIllustration,
    image,
    searchResultsDmParameters,
    id,
    coveo,
    noResultsComponent,
    errorComponent,
    vpcBannerComponent,
    filterLabel,
    filterAriaLabel,
    filterLegendLabelList,
    filterLegendLabelMap,
    resetAllLabel,
    resetAllAriaLabel,
    seeResultsLabel,
    seeResultsAriaLabel,
    openNowLabel,
    openNowAriaLabel,
    smallBreakpoint,
    largeBreakpoint,
    googleMaps,

    locationCategory,
}) => {
    return (
        <GoogleMapsLoader apiKey={googleMaps.apiKey} waitForMaps>
            <GeolocationProvider>
                <LocationUIContextProvider
                    value={{
                        title,
                        description,
                        searchLabel,
                        searchErrorMessage,
                        zipLabel,
                        zipErrorMessage,
                        filterLabel,
                        filterAriaLabel,
                        filterLegendLabelList:
                            filterLegendLabelList || 'Filters & sorting apply upon selection.',
                        filterLegendLabelMap:
                            filterLegendLabelMap || 'Filters apply upon selection.',
                        resetAllLabel,
                        resetAllAriaLabel,
                        seeResultsLabel,
                        seeResultsAriaLabel,
                        openNowLabel,
                        openNowAriaLabel,
                        smallBreakpoint: {
                            ...smallBreakpoint,
                            mapView: {
                                label: smallBreakpoint?.mapView?.label || 'Map View',
                                ariaLabel: smallBreakpoint?.mapView?.ariaLabel || 'Map View',
                            },
                            listView: {
                                label: smallBreakpoint?.listView?.label || 'List View',
                                ariaLabel: smallBreakpoint?.listView?.ariaLabel || 'List View',
                            },
                        },
                        largeBreakpoint: {
                            ...largeBreakpoint,
                            mapView: {
                                label: largeBreakpoint?.mapView?.label || 'Map View',
                                ariaLabel: largeBreakpoint?.mapView?.ariaLabel || 'Map View',
                            },
                            listView: {
                                label: largeBreakpoint?.listView?.label || 'List View',
                                ariaLabel: largeBreakpoint?.listView?.ariaLabel || 'List View',
                            },
                        },
                        googleMaps,
                        isLocationCategory: !!locationCategory,
                    }}
                >
                    <LocationStateContextProvider
                        initialValue={!locationCategory ? 'default' : 'results'}
                    >
                        <LocationSearchProvider
                            id={id}
                            coveo={coveo}
                            locationCategory={locationCategory}
                            searchResultsDmParameters={searchResultsDmParameters}
                            noResultsComponent={noResultsComponent}
                            errorComponent={errorComponent}
                            vpcBannerComponent={vpcBannerComponent}
                        >
                            <LocationFilterContextProvider>
                                <section className={styles['location-search']}>
                                    <div className={styles['location-search__header']}>
                                        <LocationHero
                                            state={state}
                                            breadcrumbs={breadcrumbs}
                                            typeaheadFixedItem={typeAheadFixedItem}
                                            illustration={illustration}
                                            hideMobileIllustration={hideMobileIllustration}
                                            image={image}
                                            googleMaps={googleMaps}
                                        />
                                    </div>
                                    <LocationMain analytics={{ componentTitle: title }} />
                                </section>
                            </LocationFilterContextProvider>
                        </LocationSearchProvider>
                    </LocationStateContextProvider>
                </LocationUIContextProvider>
            </GeolocationProvider>
        </GoogleMapsLoader>
    );
};

LocationSearch.propTypes = {
    vpcBannerComponent: PropTypes.node,
};

export default LocationSearch;
